// Builds the NetSuite create payloads (assembly item + BOM + routing) for a
// finished configuration — the web-app port of the CPQ "Item Creation Rules"
// (the Dynamic <Housing> Type Assembly Creators in itcrs.json).
//
// This is PURE and side-effect free: it returns what *would* be created. The
// actual write to NetSuite happens via the proxy (api/create-assembly) and is
// dry-run only for now — nothing here touches NetSuite.
//
// Sources:
//   - Item   : the ITCR whose rule matches HOUSING; its field "mapping" with
//              {QUESTION/ANSWER} tokens resolved from the current answers.
//   - BOM     : active materials that carry an item (materials ruleset).
//   - Routing : routing rows whose rule matches ROUTING_RULE (set by op 357).

import { evalRule } from "./rule";
// Type only, and from profile.ts rather than the products barrel, so this stays a
// compile-time dependency and cannot create an import cycle.
import type { ProductProfile } from "../products/profile";
import type { AnswerState } from "../types";

type Rec = Record<string, unknown>;

export type AssemblyData = {
  itcrs: Rec[];
  materials: Rec[];
  routings: Rec[];
  /** The running product's ProductProfile.fingerprintAnswerKey. Omitted = no check. */
  fingerprintKey?: string;
  /**
   * The running product's ProductProfile.computeRouting, when it has one. Present means
   * the routing is computed from the built item's fields instead of filtered out of
   * `routings`; see ProductProfile.computeRouting.
   */
  computeRouting?: ProductProfile["computeRouting"];
  /** The running product's ProductProfile.bomFallbackComponents, if it declares any. */
  bomFallbackComponents?: ProductProfile["bomFallbackComponents"];
  /** ProductProfile.nestedChildWhen -- when true, the BOM is a child assembly plus the
   *  rows named by nestedKeepRowPrefixes. See the profile for why. */
  nestedChildWhen?: ProductProfile["nestedChildWhen"];
  /** ProductProfile.nestedKeepRowPrefixes. */
  nestedKeepRowPrefixes?: ProductProfile["nestedKeepRowPrefixes"];
  /** The running product's ProductProfile.expectedComponentFamilies, if it declares any. */
  expectedComponentFamilies?: ProductProfile["expectedComponentFamilies"];
  /**
   * The running product's ProductProfile.buildsRouting. False means this configurator
   * has no manufacturing routing at all, so none is built and none is missing.
   */
  buildsRouting?: ProductProfile["buildsRouting"];
};

export type RoutingOp = {
  opseq: string;
  name: string;
  setupTime: string;
  runRate: string;
  workCenterId: string;
  workCenter: string;
  costTemplateId: string;
  costTemplate: string;
  /**
   * How this step connects to the previous one ("SS" = start-to-start). Set on every
   * step except the first by a computed routing; the ruleset path leaves it undefined
   * and NetSuite applies its own default.
   */
  connectionType?: string;
};

export type AssemblyPreview = {
  matchedItcr: string | null;
  item: {
    itemid: string;
    bomName: string;
    routeName: string;
    revision: string;
    subsidiary: unknown;
    fields: Record<string, string>;
  } | null;
  bom: {
    name: string;
    components: Array<{ item: string; qty: string }>;
    subsidiaryIds: string[];
    revisionName: string;
    revisionMemo: string;
    memo: string;
  };
  /**
   * Component families this configuration needs but has no item mapped for. Non-empty means
   * the BOM is incomplete, its name carries the leading "!" and whoever maintains the part
   * mapping needs telling — see ProductProfile.expectedComponentFamilies.
   */
  missingComponents: string[];
  /** True when bom.components carries NESTED_CHILD_ITEM: the caller must build the child
   *  assembly and substitute its internal id before the revision is written. */
  needsNestedChild: boolean;
  routing: {
    name: string;
    operations: RoutingOp[];
    subsidiaryId: string;
    locationId: string;
    memo: string;
  };
  warnings: string[];
};

// CPQ SYSTEM values: not questions, so no export contains them, but rules are allowed
// to reference them. Building an assembly IS the final step of a configuration, which is
// exactly what CFGSTATUS = "FINAL" means, so it is supplied here.
//
// Pilot's only item-creation rule is SOLE/CFGSTATUS[="FINAL"]. Without this the accessor
// returns undefined, the rule is permanently false, no ITCR matches and NO ITEM IS EVER
// BUILT for that product. Natural Draft, Flame Cells and Flange reference no SOLE/* key
// at all, so seeding it cannot change their rule evaluation.
const CPQ_SYSTEM_ANSWERS: Readonly<Record<string, string>> = {
  "SOLE/CFGSTATUS": "FINAL",
};

/** Stands in for the child assembly's internal id in a two-level BOM, until the caller
 *  resolves it.  Deliberately not a number: anything that writes it to NetSuite unresolved
 *  fails loudly on a non-numeric item id rather than quietly filing the wrong component. */
export const NESTED_CHILD_ITEM = "__NESTED_CHILD__";

export function buildAssembly(answers: AnswerState, data: AssemblyData): AssemblyPreview {
  const warnings: string[] = [];
  // System values first so a real answer of the same name would win, not the other way
  // round -- nothing should be able to shadow a user's answer.
  const state = { ...CPQ_SYSTEM_ANSWERS, ...answers } as AnswerState;
  const a = state as Rec;
  // Resolve {QUESTION/ANSWER} tokens against the current answers.
  const interp = (s: unknown) =>
    String(s ?? "").replace(/\{([^}]+)\}/g, (_m, key) => String(a[key] ?? ""));

  // 1) Pick the ITCR whose parsed rule matches (selects by housing type).
  const itcr =
    data.itcrs.find((i) =>
      evalRule(String(i.custrecord_cpqc_itcr_prule ?? ""), state)
    ) ?? null;
  if (!itcr) {
    // Product-neutral: HOUSING is Natural Draft's selector, and naming it sent a Pilot
    // user hunting for a question their configurator does not have (its ITCR is gated on
    // SOLE/CFGSTATUS instead).
    warnings.push(
      "No item-creation rule (ITCR) matches this configuration — check the answers its rules depend on."
    );
  }

  // The configuration's generated id. WHICH answer holds it differs per configurator
  // (Natural Draft asks HIDDEN_ID_KEY/ID_KEY, Flame Cells asks ID_KEY/ID), so the
  // caller passes the key from that product's profile. Hard-coding Natural Draft's
  // made this warn on every Flame Cell config even when its id was present.
  const idKey = data.fingerprintKey ? String(a[data.fingerprintKey] ?? "") : "";
  if (data.fingerprintKey && !idKey) {
    warnings.push(`${data.fingerprintKey} is empty — calculate the configuration first.`);
  }

  // 2) Item: ITCR field mapping with tokens resolved.
  let item: AssemblyPreview["item"] = null;
  if (itcr) {
    let mapping: Record<string, string> = {};
    try {
      const parsed = JSON.parse(String(itcr.custrecord_cpqc_itcr_fields ?? "{}"));
      mapping = (parsed && parsed.mapping) || {};
    } catch {
      warnings.push("Could not parse the ITCR field mapping JSON.");
    }
    const fields: Record<string, string> = {};
    for (const [k, v] of Object.entries(mapping)) fields[k] = interp(v);
    item = {
      itemid: interp(itcr.custrecord_cpqc_itcr_itemid),
      bomName: interp(itcr.custrecord_cpqc_itcr_bom_name),
      routeName: interp(itcr.custrecord_cpqc_itcr_rt_name),
      revision: String(itcr.custrecord_cpqc_itcr_rev_name ?? ""),
      subsidiary: itcr.custrecord_cpqc_itcr_bom_sub,
      fields,
    };
  }

  // Does this ITCR even ASK for a BOM / a routing? Both are ITCR flags, and ignoring
  // them warned about things the product never wanted. Pilot's ITCR sets routing:false,
  // so the missing-routing warning was blocking a submit over a routing it does not want.
  // Absent ITCR (nothing matched) is treated as wanting both, so the real problem stays
  // the missing ITCR rather than being buried under extra warnings.
  const wantsBom = itcr ? itcr.custrecord_cpqc_itcr_bom !== false : true;
  // ANDed with the product's own declaration (ProductProfile.buildsRouting), so EITHER
  // source can say "no routing" and neither can force one on. Flange needs that: it has
  // no route, but both its ITCRs say routing:true.
  const wantsRouting =
    data.buildsRouting !== false && (itcr ? itcr.custrecord_cpqc_itcr_routing !== false : true);

  // 3) BOM components: active materials that carry an item.
  const rulesetComponents = data.materials
    .filter(
      (m) =>
        m.custrecord_cpqc_material_item &&
        evalRule(String(m.custrecord_cpqc_material_parsed_rule ?? ""), state)
    )
    .map((m) => ({
      item: String(m.custrecord_cpqc_material_item),
      // INTERPOLATED, like the ITCR field templates above, because a quantity is not always
      // a constant: Flame Cells buys fin stock by the pound, so its rows carry
      // "{CELL_WEIGHT/CELL_WEIGHT}" and the weight is computed per configuration. A row with
      // a plain number is unaffected -- there is no token to substitute.
      qty: interp(m.custrecord_cpqc_material_item_qty ?? "1"),
    }));
  // A quantity that is not a positive number is DROPPED, loudly. api/create-assembly does
  // `bomQuantity: Number(c.qty) || 1`, so a blank one does not remove the line -- it ships as
  // 1, which for a computed quantity means 1 lb of fin stock or 1 core plug on a
  // configuration that has not worked out its real figure yet. Each computed row also guards
  // itself in its own rule; this is the backstop for the ones that do not, and for any row
  // added later that forgets.
  const droppedForQty = rulesetComponents.filter((c) => !(Number(c.qty) > 0));
  for (const c of droppedForQty) {
    warnings.push(
      `BOM component ${c.item} was left out: its quantity is "${c.qty}", which is not a positive number.`
    );
  }
  const positiveComponents = rulesetComponents.filter((c) => Number(c.qty) > 0);
  // FALLBACK, only when the ruleset produced nothing (see
  // ProductProfile.bomFallbackComponents). Deliberately a fallback rather than an
  // addition: the moment the product's export grows the material row it is standing in
  // for, this stops being used and cannot double up the component.
  // Copied out of the readonly profile array so the preview's own list stays mutable and
  // nothing downstream can write back into a product's declaration.
  let components =
    positiveComponents.length > 0 ? positiveComponents : [...(data.bomFallbackComponents ?? [])];

  // TWO-LEVEL BOM (see ProductProfile.nestedChildWhen).  A plated flame cell is the child
  // cell plus the plate; the material lines belong to the child.  The child's item id is not
  // knowable here -- it needs the pipeline re-run with the plate set to None, and a NetSuite
  // find-or-create -- so a placeholder goes in and the caller substitutes the real id.
  let needsNestedChild = false;
  if (data.nestedChildWhen && evalRule(data.nestedChildWhen, state)) {
    const keep = data.nestedKeepRowPrefixes ?? [];
    const keptRows = new Set(
      data.materials
        .filter(
          (m) =>
            m.custrecord_cpqc_material_item &&
            keep.some((prefix) => String(m.name ?? "").startsWith(prefix)) &&
            evalRule(String(m.custrecord_cpqc_material_parsed_rule ?? ""), state)
        )
        .map((m) => String(m.custrecord_cpqc_material_item))
    );
    components = [
      { item: NESTED_CHILD_ITEM, qty: "1" },
      ...components.filter((c) => keptRows.has(String(c.item))),
    ];
    needsNestedChild = true;
  }

  // Which expected component families did this configuration need but not get? A family is
  // satisfied when a MATCHED material row's name starts with one of its prefixes -- matched,
  // not merely present, so a row whose rule did not fire does not count as covered.
  const matchedRowNames = data.materials
    .filter(
      (m) =>
        m.custrecord_cpqc_material_item &&
        evalRule(String(m.custrecord_cpqc_material_parsed_rule ?? ""), state) &&
        Number(interp(m.custrecord_cpqc_material_item_qty ?? "1")) > 0
    )
    .map((m) => String(m.name ?? ""));
  const missingComponents = (data.expectedComponentFamilies ?? [])
    .filter((f) => evalRule(f.requiredWhen, state))
    .filter((f) => !matchedRowNames.some((n) => f.rowPrefixes.some((p) => n.startsWith(p))))
    .map((f) => f.label);
  // DELIBERATELY NOT a warning. SaveSubmitButton refuses to submit while preview.warnings is
  // non-empty, and a missing part must not block: the BOM is meant to be created, marked "!"
  // and reported by email (Kevin, 8/21). Blocking would mean nobody could build the cell at
  // all -- a worse outcome than building it short and saying so. missingComponents is its own
  // field for exactly that reason.
  // The "!" prefix is how an incomplete BOM announces itself in NetSuite (Kevin, 8/21: "if a
  // part is missing ... add ! to the begining of the bom name if not then do not do !").
  // Applied to item.bomName, which bom.name is taken from, so the two cannot disagree.
  if (item && missingComponents.length > 0 && !item.bomName.startsWith("!")) {
    item = { ...item, bomName: "!" + item.bomName };
  }
  if (wantsBom && !components.length) {
    warnings.push("No BOM components matched the materials ruleset.");
  }

  // 4) Routing operations.
  //
  // Two sources, and the product decides which (see ProductProfile.computeRouting):
  // filter the routing RULESETS, or COMPUTE the steps from the item fields the ITCR just
  // built. Flame Cells needs the second because its routing came from a labor model in a
  // User Event script -- rolling time as a function of die, material and OD -- which no
  // per-row rule can express, and its routings.json is empty.
  //
  // A computed routing that declines (ok:false) reports WHY as the warning, which is the
  // whole point: "blank/unmapped Die" is actionable and "No routing operations matched
  // ROUTING_RULE" is not.
  let computedSubsidiaryId: string | null = null;
  let computedLocationId: string | null = null;
  let computedOps: RoutingOp[] | null = null;
  if (!wantsRouting) {
    // The ITCR does not want a routing, so do not build one and do not complain about
    // its absence.
    computedOps = [];
  } else if (data.computeRouting) {
    if (!item?.itemid) {
      // No item means no fields to compute from; the missing-ITCR warning above already
      // says why, so do not pile on a second one.
      computedOps = [];
    } else {
      const res = data.computeRouting({ itemid: item.itemid, fields: item.fields });
      if (res.ok) {
        computedOps = res.operations;
        computedSubsidiaryId = res.subsidiaryId;
        computedLocationId = res.locationId;
      } else {
        computedOps = [];
        warnings.push(`No routing built: ${res.reason}`);
      }
    }
  }

  const rulesetOperations = data.routings
    .filter((r) =>
      evalRule(String(r.custrecord_cpqc_routing_parsed_rule ?? ""), state)
    )
    .map((r) => ({
      opseq: String(r.custrecord_cpqc_routing_opseq ?? ""),
      name: String(
        r.custrecord_cpqc_routing_am_oper_name ||
          r.custrecord_cpqc_routing_description ||
          r.name ||
          ""
      ),
      setupTime: String(r.custrecord_cpqc_routing_stime ?? ""),
      runRate: String(r.custrecord_cpqc_routing_rrate ?? ""),
      workCenterId: String(r.custrecord_cpqc_routing_mwc ?? ""),
      workCenter: String(r.custrecord_cpqc_routing_mwcn ?? ""),
      costTemplateId: String(r.custrecord_cpqc_routing_mct ?? ""),
      costTemplate: String(r.custrecord_cpqc_routing_mctn ?? ""),
    }))
    .sort((x, y) => Number(x.opseq) - Number(y.opseq));

  const operations = computedOps ?? rulesetOperations;
  // Only the RULESET path of a product that WANTS a routing gets the ruleset wording. A
  // computed routing that came back empty has already explained itself above, and a
  // product whose ITCR sets routing:false is not missing anything.
  if (wantsRouting && !operations.length && !data.computeRouting) {
    warnings.push("No routing operations matched ROUTING_RULE.");
  }

  // 5) BOM/routing metadata from the ITCR (subsidiary, location, revision, memos).
  let bomSubs: string[] = [];
  try {
    const parsed = JSON.parse(String(itcr?.custrecord_cpqc_itcr_bom_sub ?? "[]"));
    if (Array.isArray(parsed)) bomSubs = parsed.map((x) => String(x));
  } catch {
    /* leave empty */
  }

  return {
    matchedItcr: itcr ? String(itcr.name ?? "") : null,
    item,
    missingComponents,
    // True when bom.components carries NESTED_CHILD_ITEM and the caller must substitute a
    // real internal id before the revision is written.
    needsNestedChild,
    bom: {
      name: item?.bomName ?? "",
      components,
      subsidiaryIds: bomSubs,
      revisionName: String(itcr?.custrecord_cpqc_itcr_rev_name ?? "Rev A"),
      revisionMemo: String(itcr?.custrecord_cpqc_itcr_rev_memo ?? ""),
      memo: String(itcr?.custrecord_cpqc_itcr_bom_memo ?? ""),
    },
    routing: {
      name: item?.routeName ?? "",
      operations,
      // A computed routing carries its own subsidiary/location (Flame Cells: Parent
      // Company + Flameco Main - Building 1, from the script it replaces). The ITCR's
      // rt_loc is blank for that product, so taking the ITCR value would drop the
      // location the shop floor schedules against.
      subsidiaryId: computedSubsidiaryId ?? String(itcr?.custrecord_cpqc_itcr_rt_sub ?? ""),
      locationId: computedLocationId ?? String(itcr?.custrecord_cpqc_itcr_rt_loc ?? ""),
      memo: String(itcr?.custrecord_cpqc_itcr_rt_memo ?? ""),
    },
    warnings,
  };
}
